import { yupResolver } from '@hookform/resolvers/yup';
// styles
import classnames from 'classnames/bind';
import Button from 'components/Button';
import RemoveButton from 'components/Button/RemoveButton';
import Input from 'components/Input';
// components
import RemoveModal from 'components/instances/RemoveModal';
import Typography from 'components/Typography';
// containers
import ManagementWrapper from 'containers/ManagementWrapper';
// utils
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ClASSESS_FULL, getManageClassRoute, getResetUserPasswordRoute } from 'routes';
import { notifyWithSnack } from 'store/actions/notifications';
import { clearStudentInfo, getStudent, removeStudent, updateStudent } from 'store/actions/user';
import * as yup from 'yup';

import styles from './ManageUser.module.scss';

const cx = classnames.bind(styles);

const ManageUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const { classId, userId } = useParams();
  const { currClass = {}, studentInfo = {} } = useSelector(({ user }) => {
    const currClass = user.classrooms.find(({ id }) => +id === +classId);

    return { currClass, studentInfo: user.updatedStudent };
  });
  const firstName = studentInfo.first_name || '';
  const lastName = studentInfo.last_name_initial || '';
  const studentName =
    firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName ? `${firstName || lastName}` : 'No Name';

  const crumbs = {
    'all classes': ClASSESS_FULL,
    [currClass.name]: getManageClassRoute(classId),
    [studentName]: '',
  };

  const schema = yup.object().shape({
    firstName: yup.string(),
    lastInitial: yup.mixed().test({
      test(value) {
        if (/^[A-Za-z]{1,2}$/m.test(value) || lastName.length === 0) return true;
        return this.createError({ message: 'Please provide at least one letter.', path: this.path });
      },
    }),
    username: yup.string().required('Username is required.').min(3, 'Username must be at least 3 characters.'),
    ...(resetPassword && {
      newPassword: yup
        .string()
        .required('New password field is required.')
        .min(8, 'Password must be at least 8 characters.')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*(\d|\W)).{8,}$/,
          'Passwords must include an upper and lowercase letter, and at least one number or special character.',
        ),
    }),
    ...(resetPassword && {
      repeatPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Passwords must match.'),
    }),
  });

  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors },
    reset,
    setError,
  } = useForm({ resolver: yupResolver(schema), mode: 'onChange' });

  const [serverError, setServerError] = useState('');

  const handleFormSubmit = ({ firstName, lastInitial, username, newPassword }) => {
    dispatch(
      updateStudent(userId, classId, {
        first_name: firstName,
        last_name_initial: lastInitial.toUpperCase(),
        username,
        ...(newPassword && { password: newPassword }),
      }),
    )
      .then(() => {
        navigate(getManageClassRoute(classId));
        dispatch(notifyWithSnack({ message: `${username} has been updated.` }));
        reset(); // Reset the form after successful submission
      })
      .catch((error) => {
        const formattedError = error.replace('<![CDATA[', '').replace(']]>', '');

        if (formattedError.toLowerCase().includes('username')) {
          setError('username', {
            type: 'server',
            message: formattedError,
          });
        } else {
          setServerError(formattedError);
        }
      });
  };

  const handleRemoveStudent = () =>
    dispatch(removeStudent(userId, classId)).then(() => navigate(getManageClassRoute(classId)));

  const handleOpenResetPassword = () => setResetPassword(true);
  const handleCancel = () => setResetPassword(false);

  const changePasswordRenderer = () => (
    <Typography className={cx('change-password')} onClick={handleOpenResetPassword}>
      Change Password
    </Typography>
  );

  useEffect(() => {
    !!userId && dispatch(getStudent(userId));

    return () => dispatch(clearStudentInfo());
  }, []);

  useEffect(() => {
    if (!isEmpty(studentInfo) && !isEmpty(currClass)) {
      reset({
        firstName: studentInfo.first_name || '',
        lastInitial: studentInfo.last_name_initial || '',
        username: studentInfo.username || '',
      });
    }
  }, [studentInfo, currClass]);

  return (
    <ManagementWrapper breadcrumbs={crumbs}>
      <div className={cx('heading-wrapper')}>
        <Typography type="h4">{studentName || 'No Name'}</Typography>
        <RemoveButton onClick={() => setModalOpen(true)} />
      </div>

      <form className={cx('form-container', 'mgt-35')} onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={cx('input-top-row')}>
          <Input
            className={cx('first-name')}
            label="First Name"
            name="firstName"
            inputRef={register('firstName', { required: true })}
            errors={errors}
          />
          <Input
            className={cx('last-initial')}
            label="Last Initial"
            name="lastInitial"
            inputRef={register('lastInitial', { required: true })}
            errors={errors}
            maxLength={2}
          />
        </div>

        <Input label="Username" name="username" inputRef={register('username', { required: true })} errors={errors} />

        {!resetPassword && (
          <Input label="Password" actionOptionRenderer={changePasswordRenderer} name="password" disabled />
        )}

        {resetPassword && (
          <>
            <div className={styles.passwordActions}>
              <Typography>Change Password</Typography>

              <Typography className={styles.passwordCancel} onClick={handleCancel}>
                Cancel
              </Typography>
            </div>

            <div className={styles.passwordWrapper}>
              <Input
                label="Enter new password"
                name="newPassword"
                type="password"
                inputRef={register('newPassword', { required: true })}
                errors={errors}
                isPassword
              />
              <Input
                label="Re-enter new password"
                name="repeatPassword"
                type="password"
                inputRef={register('repeatPassword', { required: true })}
                errors={errors}
                isPassword
              />
            </div>
          </>
        )}
        {/* Display the error message below the form */}
        {serverError && (
          <Typography type="body-xs" className={cx('error')}>
            {serverError}
          </Typography>
        )}
        <Button className={cx('submit-button', 'mgt-35')} label="SAVE" submit />
      </form>

      <RemoveModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        subject={studentInfo.first_name || studentInfo.username}
        onConfirm={handleRemoveStudent}
      />
    </ManagementWrapper>
  );
};

export default ManageUser;
